'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';

interface Language {
  locale: string;
  name: string;
  flag: string;
}

interface LanguageSwitcherClientProps {
  direction: 'up' | 'down';
  currentLanguage: Language;
  languages: Language[];
}

// Create a global cache for path translations
// This persists between renders but is cleared on page refreshes
const pathTranslationCache: Record<string, string> = {};

export function LanguageSwitcherClient({
  direction,
  currentLanguage,
  languages,
}: LanguageSwitcherClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const switchLocale = async (locale: string) => {
    if (locale === currentLanguage.locale || !pathname) return;
    
    try {
      setIsLoading(prev => ({ ...prev, [locale]: true }));
      
      // Create a cache key for this specific translation
      const cacheKey = `${currentLanguage.locale}-${locale}-${pathname}`;
      
      // Check if we already have this translation in cache
      let translatedPath;
      if (pathTranslationCache[cacheKey]) {
        translatedPath = pathTranslationCache[cacheKey];
      } else {
        // If not in cache, fetch from API
        const response = await fetch(
          `/api/translate-path?currentLocale=${currentLanguage.locale}&targetLocale=${locale}&pathname=${encodeURIComponent(pathname)}`
        );
        
        const data = await response.json();
        translatedPath = data.translatedPath;
        
        // Store in cache for future use
        pathTranslationCache[cacheKey] = translatedPath;
      }
      
      setIsOpen(false);
      router.push(translatedPath);
    } catch {
      router.push(`/${locale}`);
    } finally {
      setIsLoading(prev => ({ ...prev, [locale]: false }));
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="relative inline-block text-left"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-2 py-1 border border-primary/50 rounded-full shadow-sm bg-bg-main/10 backdrop-blur-sm focus:outline-none"
      >
        <span className="text-xl">{currentLanguage.flag}</span>
      </button>
      {isOpen && (
        <div
          className={`${
            direction === 'up'
              ? 'origin-bottom-right absolute -right-28 bottom-full mb-2'
              : 'origin-top-right absolute right-0 top-full mt-2'
          } w-32 rounded-md shadow-lg bg-bg-main border border-primary backdrop-blur-sm`}
        >
          <div className="py-1">
            {languages.map((lang) => (
              <button
                key={lang.locale}
                onClick={() => switchLocale(lang.locale)}
                disabled={isLoading[lang.locale]}
                className="flex items-center w-full px-4 py-2 text-sm text-text-main hover:text-primary disabled:opacity-50"
              >
                <span className="mr-2 text-xl">{lang.flag}</span>
                {lang.name}
                {isLoading[lang.locale] && (
                  <span className="ml-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 